import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Table,
  Input,
  Collapse,
  Spinner,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  getCompanyCheckListProgress,
  updateCheckListProgress,
  initializeCompanyCheckList,
  CompanyCheckListProgress,
} from '../../../services/checkListService';

interface Phase3CheckListFormProps {
  onSubmit: (values: any) => void;
  onPrevious: () => void;
  companyId: string;
  isSubmitting?: boolean;
  canReopenCompletedTasks?: boolean; // Permission to reopen completed tasks
  isFinalPhase?: boolean;
}

interface GroupedProgress {
  [topicName: string]: CompanyCheckListProgress[];
}

const Phase3CheckListForm: React.FC<Phase3CheckListFormProps> = ({
  onSubmit,
  onPrevious,
  companyId,
  isSubmitting = false,
  canReopenCompletedTasks = false,
  isFinalPhase = false,
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<CompanyCheckListProgress[]>([]);
  const [groupedProgress, setGroupedProgress] = useState<GroupedProgress>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTopics, setExpandedTopics] = useState<{ [key: string]: boolean }>({});
  const [updatingItems, setUpdatingItems] = useState<{ [key: string]: boolean }>({});
  const [hasAttemptedInit, setHasAttemptedInit] = useState(false);
  const [notesTimers, setNotesTimers] = useState<{ [key: string]: NodeJS.Timeout }>({});
  const [statusFilters, setStatusFilters] = useState<Set<string>>(
    new Set(['completed', 'late', 'warning', 'on-time'])
  );

  const loadCheckListProgress = async () => {
    // Don't load if companyId is empty (during new company creation)
    if (!companyId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await getCompanyCheckListProgress(companyId);
      setProgress(data);

      // Group by topic
      const grouped: GroupedProgress = {};
      data.forEach((item) => {
        const topicName = item.topicName || 'Outros';
        if (!grouped[topicName]) {
          grouped[topicName] = [];
        }
        grouped[topicName].push(item);
      });

      // Sort items within each topic by display order
      Object.keys(grouped).forEach((topicName) => {
        grouped[topicName].sort(
          (a, b) => (a.itemDisplayOrder || 0) - (b.itemDisplayOrder || 0)
        );
      });

      setGroupedProgress(grouped);

      // Collapse all topics by default
      const expanded: { [key: string]: boolean } = {};
      Object.keys(grouped).forEach((topicName) => {
        expanded[topicName] = false;
      });
      setExpandedTopics(expanded);
    } catch (error: any) {
      toast.error(error.message || t('Erro ao carregar checklist'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setHasAttemptedInit(false); // Reset flag when company changes
    loadCheckListProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(notesTimers).forEach((timer) => clearTimeout(timer));
    };
  }, [notesTimers]);

  // Auto-initialize checklist if empty (handles legacy companies)
  useEffect(() => {
    const initializeIfEmpty = async () => {
      // Don't initialize if companyId is empty (during new company creation)
      if (!companyId) return;

      // Only attempt initialization once, when loading is done and no items exist
      if (!isLoading && progress.length === 0 && !hasAttemptedInit) {
        setHasAttemptedInit(true); // Set flag immediately to prevent re-entry
        try {
          toast.info(t('Inicializando checklist...'));
          await initializeCompanyCheckList(companyId);
          // Reload checklist after initialization
          await loadCheckListProgress();
          toast.success(t('Checklist inicializado com sucesso!'));
        } catch (error: any) {
          // Only log error, don't block user
          console.error('Failed to auto-initialize checklist:', error);
          toast.warning(t('Não foi possível inicializar o checklist automaticamente'));
        }
      }
    };

    initializeIfEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, progress.length, hasAttemptedInit, companyId]);

  const handleToggleItem = async (item: CompanyCheckListProgress) => {
    const itemKey = item.checkListItemId;
    setUpdatingItems((prev) => ({ ...prev, [itemKey]: true }));

    try {
      await updateCheckListProgress(companyId, {
        checkListItemId: item.checkListItemId,
        isCompleted: !item.isCompleted,
        isDisregarded: false, // Always set to false when toggling completed
        notes: item.notes,
      });

      // Update local state
      setProgress((prev) =>
        prev.map((p) =>
          p.checkListItemId === item.checkListItemId
            ? { ...p, isCompleted: !p.isCompleted, isDisregarded: false }
            : p
        )
      );

      // Update grouped progress
      setGroupedProgress((prev) => {
        const topicName = item.topicName || 'Outros';
        return {
          ...prev,
          [topicName]: prev[topicName].map((p) =>
            p.checkListItemId === item.checkListItemId
              ? { ...p, isCompleted: !p.isCompleted, isDisregarded: false }
              : p
          ),
        };
      });

      toast.success(
        item.isCompleted
          ? t('Item marcado como pendente')
          : t('Item marcado como concluído')
      );
    } catch (error: any) {
      toast.error(error.message || t('Erro ao atualizar item'));
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleToggleDisregard = async (item: CompanyCheckListProgress) => {
    const itemKey = item.checkListItemId;

    // Validate notes when marking as disregarded
    if (!item.isDisregarded) {
      const notesLength = (item.notes || '').trim().length;
      if (notesLength < 15) {
        toast.warning(
          t('Para desconsiderar um item, é necessário informar uma justificativa com pelo menos 15 caracteres no campo Observações.'),
          {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        // Focus the notes input field to guide the user
        const notesInput = document.querySelector(`input[data-item-id="${itemKey}"]`) as HTMLInputElement;
        if (notesInput) {
          notesInput.focus();
          notesInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return;
      }
    }

    setUpdatingItems((prev) => ({ ...prev, [itemKey]: true }));

    try {
      const newIsDisregarded = !item.isDisregarded;
      // When marking as disregarded, also mark as completed (backend will handle this too)
      const newIsCompleted = newIsDisregarded ? true : false;

      await updateCheckListProgress(companyId, {
        checkListItemId: item.checkListItemId,
        isCompleted: newIsCompleted,
        isDisregarded: newIsDisregarded,
        notes: item.notes,
      });

      // Update local state
      setProgress((prev) =>
        prev.map((p) =>
          p.checkListItemId === item.checkListItemId
            ? { ...p, isCompleted: newIsCompleted, isDisregarded: newIsDisregarded }
            : p
        )
      );

      // Update grouped progress
      setGroupedProgress((prev) => {
        const topicName = item.topicName || 'Outros';
        return {
          ...prev,
          [topicName]: prev[topicName].map((p) =>
            p.checkListItemId === item.checkListItemId
              ? { ...p, isCompleted: newIsCompleted, isDisregarded: newIsDisregarded }
              : p
          ),
        };
      });

      toast.success(
        item.isDisregarded
          ? t('Item marcado como pendente')
          : t('Item marcado como desconsiderado e concluído')
      );
    } catch (error: any) {
      toast.error(error.message || t('Erro ao atualizar item'));
    } finally {
      setUpdatingItems((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleNotesChange = (
    item: CompanyCheckListProgress,
    notes: string
  ) => {
    const itemKey = item.checkListItemId;

    // Update local state immediately (optimistic update)
    setProgress((prev) =>
      prev.map((p) =>
        p.checkListItemId === itemKey ? { ...p, notes } : p
      )
    );

    // Update grouped progress
    setGroupedProgress((prev) => {
      const topicName = item.topicName || 'Outros';
      return {
        ...prev,
        [topicName]: prev[topicName].map((p) =>
          p.checkListItemId === itemKey ? { ...p, notes } : p
        ),
      };
    });

    // Clear existing timer for this item
    if (notesTimers[itemKey]) {
      clearTimeout(notesTimers[itemKey]);
    }

    // Set new debounced timer to save after 1 second of no typing
    const timer = setTimeout(async () => {
      try {
        await updateCheckListProgress(companyId, {
          checkListItemId: itemKey,
          isCompleted: item.isCompleted,
          notes,
        });
      } catch (error: any) {
        toast.error(error.message || t('Erro ao atualizar observações'));
        // Revert optimistic update on error
        setProgress((prev) =>
          prev.map((p) =>
            p.checkListItemId === itemKey ? { ...p, notes: item.notes } : p
          )
        );
        setGroupedProgress((prev) => {
          const topicName = item.topicName || 'Outros';
          return {
            ...prev,
            [topicName]: prev[topicName].map((p) =>
              p.checkListItemId === itemKey ? { ...p, notes: item.notes } : p
            ),
          };
        });
      }
    }, 1000);

    setNotesTimers((prev) => ({ ...prev, [itemKey]: timer }));
  };

  const toggleTopic = (topicName: string) => {
    setExpandedTopics((prev) => ({ ...prev, [topicName]: !prev[topicName] }));
  };

  const expandAllTopics = () => {
    const expanded: { [key: string]: boolean } = {};
    Object.keys(groupedProgress).forEach((topicName) => {
      expanded[topicName] = true;
    });
    setExpandedTopics(expanded);
  };

  const collapseAllTopics = () => {
    const collapsed: { [key: string]: boolean } = {};
    Object.keys(groupedProgress).forEach((topicName) => {
      collapsed[topicName] = false;
    });
    setExpandedTopics(collapsed);
  };

  const getTopicProgress = (items: CompanyCheckListProgress[]) => {
    const completed = items.filter((item) => item.isCompleted).length;
    const total = items.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  const getTotalProgress = () => {
    const completed = progress.filter((item) => item.isCompleted).length;
    const total = progress.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  const getStatusCounts = () => {
    const counts = {
      completed: 0,
      late: 0,
      warning: 0,
      'on-time': 0,
    };

    progress.forEach((item) => {
      const status = calculateDeadlineStatus(item).status;

      // Count "waiting" items as "on-time" since they're just waiting for dependencies
      if (status === 'waiting') {
        counts['on-time']++;
      } else if (status in counts) {
        counts[status as keyof typeof counts]++;
      }
    });

    return counts;
  };

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  const showAllStatuses = () => {
    setStatusFilters(new Set(['completed', 'late', 'warning', 'on-time']));
  };

  const hideAllStatuses = () => {
    setStatusFilters(new Set([]));
  };

  const filterItemsByStatus = (items: CompanyCheckListProgress[]) => {
    if (statusFilters.size === 4) return items; // All filters active, show all
    if (statusFilters.size === 0) return []; // No filters active, show none

    return items.filter((item) => {
      const deadlineStatus = calculateDeadlineStatus(item);

      // Show "waiting" items when "on-time" filter is active
      if (deadlineStatus.status === 'waiting' && statusFilters.has('on-time')) {
        return true;
      }

      return statusFilters.has(deadlineStatus.status);
    });
  };

  const checkDependenciesMet = (item: CompanyCheckListProgress): { met: boolean; pendingItems: CompanyCheckListProgress[] } => {
    if (!item.dependencyItemIds || item.dependencyItemIds.length === 0) {
      return { met: true, pendingItems: [] };
    }

    const pendingDependencies = item.dependencyItemIds
      .map(depId => progress.find(p => p.checkListItemId === depId))
      .filter((dep): dep is CompanyCheckListProgress => dep !== undefined && !dep.isCompleted);

    return {
      met: pendingDependencies.length === 0,
      pendingItems: pendingDependencies
    };
  };

  const calculateDeadlineStatus = (item: CompanyCheckListProgress) => {
    if (item.isCompleted) {
      return { status: 'completed', label: t('Concluído'), colorClass: 'text-success', badgeClass: 'bg-success-subtle text-success' };
    }

    // Check if dependencies are met
    const dependencyCheck = checkDependenciesMet(item);
    if (!dependencyCheck.met) {
      return {
        status: 'waiting',
        label: t('Aguardando dependências'),
        colorClass: 'text-muted',
        badgeClass: 'bg-secondary-subtle text-secondary'
      };
    }

    // Calculate start time: use the latest completedAt from dependencies, or company creation date if no dependencies
    let startTime: Date;
    if (item.dependencyItemIds && item.dependencyItemIds.length > 0) {
      // Find the latest completion time among all dependencies
      const dependencyCompletionTimes = item.dependencyItemIds
        .map(depId => progress.find(p => p.checkListItemId === depId))
        .filter((dep): dep is CompanyCheckListProgress => dep !== undefined && dep.isCompleted && dep.completedAt !== null)
        .map(dep => new Date(dep.completedAt!));

      if (dependencyCompletionTimes.length === 0) {
        // Dependencies not completed yet (should not happen as checkDependenciesMet already checked this)
        return {
          status: 'waiting',
          label: t('Aguardando dependências'),
          colorClass: 'text-muted',
          badgeClass: 'bg-secondary-subtle text-secondary'
        };
      }

      // Start time is the latest completion time among dependencies
      startTime = new Date(Math.max(...dependencyCompletionTimes.map(d => d.getTime())));
    } else {
      // No dependencies - use company creation date
      if (!item.companyCreatedAt) {
        return { status: 'unknown', label: '-', colorClass: '', badgeClass: '' };
      }
      startTime = new Date(item.companyCreatedAt);
    }

    if (!item.deadlineValue || !item.deadlineUnit) {
      return { status: 'unknown', label: '-', colorClass: '', badgeClass: '' };
    }

    const now = new Date();

    // Calculate deadline based on unit
    let deadlineMs = 0;
    if (item.deadlineUnit.toLowerCase().includes('hora')) {
      deadlineMs = item.deadlineValue * 60 * 60 * 1000; // hours to milliseconds
    } else if (item.deadlineUnit.toLowerCase().includes('dia')) {
      deadlineMs = item.deadlineValue * 24 * 60 * 60 * 1000; // days to milliseconds
    }

    const dueDate = new Date(startTime.getTime() + deadlineMs);
    const timeRemaining = dueDate.getTime() - now.getTime();
    const totalTime = deadlineMs;

    // Calculate time remaining in human-readable format
    const absTimeRemaining = Math.abs(timeRemaining);
    const daysRemaining = Math.floor(absTimeRemaining / (24 * 60 * 60 * 1000));
    const hoursRemaining = Math.floor((absTimeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

    let timeLabel = '';
    if (timeRemaining < 0) {
      // Overdue
      if (daysRemaining > 0) {
        timeLabel = `${t('Atrasado')} ${daysRemaining}d ${hoursRemaining}h`;
      } else {
        timeLabel = `${t('Atrasado')} ${hoursRemaining}h`;
      }
      return {
        status: 'late',
        label: timeLabel,
        colorClass: 'text-danger fw-bold',
        badgeClass: 'bg-danger-subtle text-danger',
        daysRemaining: -daysRemaining,
        isOverdue: true
      };
    } else {
      // On time - check if approaching deadline (< 20% time remaining)
      const percentageRemaining = (timeRemaining / totalTime) * 100;

      if (daysRemaining > 0) {
        timeLabel = `${daysRemaining}d ${hoursRemaining}h ${t('restantes')}`;
      } else {
        timeLabel = `${hoursRemaining}h ${t('restantes')}`;
      }

      if (percentageRemaining < 20) {
        return {
          status: 'warning',
          label: timeLabel,
          colorClass: 'text-warning fw-bold',
          badgeClass: 'bg-warning-subtle text-warning',
          daysRemaining,
          isOverdue: false
        };
      } else {
        return {
          status: 'on-time',
          label: timeLabel,
          colorClass: 'text-muted',
          badgeClass: 'bg-secondary-subtle text-secondary',
          daysRemaining,
          isOverdue: false
        };
      }
    }
  };

  const handleSubmit = () => {
    // Just pass empty object - the checklist progress is already saved
    onSubmit({});
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2">{t('Carregando checklist...')}</p>
      </div>
    );
  }

  const totalProgress = getTotalProgress();
  const statusCounts = getStatusCounts();

  return (
    <div>
      <Card>
        <CardHeader>
          {/* Mobile Layout */}
          <div className="d-block d-md-none">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="flex-grow-1">
                <h6 className="mb-1" style={{ fontSize: '0.95rem' }}>{t('Check List de Abertura')}</h6>
                <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                  {t('Marque os itens conforme forem concluídos')}
                </p>
              </div>
              <div className="text-end ms-2">
                <small className="d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {t('Progresso')}: {totalProgress.completed}/{totalProgress.total}
                </small>
                <div className="progress" style={{ height: '6px', width: '80px' }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${totalProgress.percentage}%` }}
                    aria-valuenow={totalProgress.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-1">
              <Button
                color="light"
                size="sm"
                onClick={expandAllTopics}
                title={t('Expandir todos os tópicos')}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
              >
                <i className="ri-arrow-down-s-line me-1"></i>
                {t('Exibir')}
              </Button>
              <Button
                color="light"
                size="sm"
                onClick={collapseAllTopics}
                title={t('Recolher todos os tópicos')}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
              >
                <i className="ri-arrow-right-s-line me-1"></i>
                {t('Recolher')}
              </Button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="d-none d-md-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">{t('Check List de Abertura')}</h5>
              <p className="text-muted mb-0 mt-1">
                {t('Marque os itens conforme forem concluídos')}
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Button
                color="light"
                size="sm"
                onClick={expandAllTopics}
                title={t('Expandir todos os tópicos')}
              >
                <i className="ri-arrow-down-s-line me-1"></i>
                {t('Exibir')}
              </Button>
              <Button
                color="light"
                size="sm"
                onClick={collapseAllTopics}
                title={t('Recolher todos os tópicos')}
              >
                <i className="ri-arrow-right-s-line me-1"></i>
                {t('Recolher')}
              </Button>
            </div>
            <div className="text-end">
              <h6 className="mb-0">
                {t('Progresso')}: {totalProgress.completed}/{totalProgress.total}
              </h6>
              <div className="progress mt-2" style={{ height: '8px', width: '150px' }}>
                <div
                  className="progress-bar bg-success"
                  role="progressbar"
                  style={{ width: `${totalProgress.percentage}%` }}
                  aria-valuenow={totalProgress.percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Status Filter Bar */}
        <div className="border-top border-bottom py-2 px-3 bg-light">
          <div className="d-flex gap-1 align-items-center flex-wrap">
            <small className="text-muted me-1 d-none d-sm-inline">{t('Filtrar por status')}:</small>

            <Button
              size="sm"
              color={statusFilters.has('late') ? 'danger' : 'outline-danger'}
              onClick={() => toggleStatusFilter('late')}
              title={t('Atrasado')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              <i className="ri-alarm-warning-line me-1"></i>
              {t('Atrasado')} ({statusCounts.late})
            </Button>

            <Button
              size="sm"
              color={statusFilters.has('warning') ? 'warning' : 'outline-warning'}
              onClick={() => toggleStatusFilter('warning')}
              title={t('Alerta - menos de 20% do prazo restante')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              <i className="ri-error-warning-line me-1"></i>
              {t('Alerta')} ({statusCounts.warning})
            </Button>

            <Button
              size="sm"
              color={statusFilters.has('on-time') ? 'info' : 'outline-info'}
              onClick={() => toggleStatusFilter('on-time')}
              title={t('No Prazo')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              <i className="ri-time-line me-1"></i>
              {t('No Prazo')} ({statusCounts['on-time']})
            </Button>

            <Button
              size="sm"
              color={statusFilters.has('completed') ? 'success' : 'outline-success'}
              onClick={() => toggleStatusFilter('completed')}
              title={t('Concluído')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              <i className="ri-checkbox-circle-line me-1"></i>
              {t('Concluído')} ({statusCounts.completed})
            </Button>

            {statusFilters.size === 4 ? (
              <Button
                size="sm"
                color="link"
                onClick={hideAllStatuses}
                className="text-decoration-none"
              >
                <i className="ri-eye-off-line me-1"></i>
                {t('Desmarcar todos')}
              </Button>
            ) : statusFilters.size < 4 ? (
              <Button
                size="sm"
                color="link"
                onClick={showAllStatuses}
                className="text-decoration-none"
              >
                <i className="ri-eye-line me-1"></i>
                {t('Mostrar todos')}
              </Button>
            ) : null}
          </div>
        </div>

        <CardBody>
          {Object.keys(groupedProgress).length === 0 ? (
            <div className="text-center py-4">
              <i className="ri-file-list-line text-muted fs-1"></i>
              <p className="text-muted mt-2">
                {t('Nenhum item no checklist. Configure os itens na administração.')}
              </p>
            </div>
          ) : (
            <div className="accordion" id="checklistAccordion">
              {Object.keys(groupedProgress)
                .sort((a, b) => {
                  // Sort topics by topicDisplayOrder instead of alphabetically
                  const topicAOrder = groupedProgress[a][0]?.topicDisplayOrder || 0;
                  const topicBOrder = groupedProgress[b][0]?.topicDisplayOrder || 0;
                  return topicAOrder - topicBOrder;
                })
                .map((topicName, topicIndex) => {
                  const allItems = groupedProgress[topicName];
                  const items = filterItemsByStatus(allItems);

                  // Skip topic if no items match the filter
                  if (items.length === 0) return null;

                  const topicProgress = getTopicProgress(allItems);

                  return (
                    <Card key={topicName} className="mb-2">
                      <CardHeader
                        className="cursor-pointer"
                        onClick={() => toggleTopic(topicName)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-0">
                              <i
                                className={`ri-${
                                  expandedTopics[topicName] ? 'arrow-down' : 'arrow-right'
                                }-s-line me-2`}
                              ></i>
                              {topicName}
                            </h6>
                          </div>
                          <div className="text-end">
                            <small className="text-muted me-3">
                              {topicProgress.completed}/{topicProgress.total} ({topicProgress.percentage}%)
                            </small>
                            <div
                              className="progress d-inline-block"
                              style={{ height: '6px', width: '100px', verticalAlign: 'middle' }}
                            >
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${topicProgress.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <Collapse isOpen={expandedTopics[topicName]}>
                        <CardBody className="p-0">
                          <div className="table-responsive">
                            <Table className="table-borderless mb-0" style={{ minWidth: '900px' }}>
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: '40px' }}></th>
                                  <th style={{ minWidth: '200px' }}>{t('Item')}</th>
                                  <th style={{ width: '120px' }}>{t('Responsável')}</th>
                                  <th style={{ width: '180px' }}>{t('Prazo / Status')}</th>
                                  <th style={{ width: '80px', textAlign: 'center' }}>{t('Desconsiderar')}</th>
                                  <th style={{ width: '200px' }}>{t('Observações')}</th>
                                </tr>
                              </thead>
                            <tbody>
                              {items.map((item) => {
                                const dependencyCheck = checkDependenciesMet(item);
                                const isBlocked = !dependencyCheck.met && !item.isCompleted;

                                return (
                                <tr key={item.checkListItemId} className={isBlocked ? 'table-warning' : ''}>
                                  <td>
                                    <div className="form-check d-flex align-items-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={item.isCompleted}
                                        onChange={() => handleToggleItem(item)}
                                        disabled={updatingItems[item.checkListItemId] || isBlocked || (item.isCompleted && !canReopenCompletedTasks)}
                                        title={
                                          item.isCompleted && !canReopenCompletedTasks
                                            ? t('Tarefa concluída e bloqueada. Apenas usuários autorizados podem reabrir')
                                            : item.isCompleted && canReopenCompletedTasks
                                            ? t('Você tem permissão para reabrir esta tarefa')
                                            : isBlocked
                                            ? t('Aguardando conclusão de dependências')
                                            : ''
                                        }
                                      />
                                      {item.isCompleted && !canReopenCompletedTasks && (
                                        <i className="ri-lock-line text-warning ms-2" title={t('Tarefa bloqueada')}></i>
                                      )}
                                      {updatingItems[item.checkListItemId] && (
                                        <Spinner size="sm" color="primary" className="ms-2" />
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div
                                      className={
                                        item.isCompleted ? 'text-decoration-line-through text-muted' : ''
                                      }
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <strong>{item.itemName}</strong>
                                        {item.skiped && (
                                          <span className="badge bg-warning-subtle text-warning" title={t('Item opcional - pode ser desconsiderado com justificativa')}>
                                            <i className="ri-skip-forward-line me-1"></i>
                                            {t('Opcional')}
                                          </span>
                                        )}
                                        {isBlocked && (
                                          <span className="badge bg-danger-subtle text-danger" title={t('Aguardando dependências')}>
                                            <i className="ri-lock-line me-1"></i>
                                            Bloqueado
                                          </span>
                                        )}
                                      </div>
                                      {item.description && (
                                        <p className="text-muted mb-0 small">{item.description}</p>
                                      )}
                                      {isBlocked && (
                                        <div className="alert alert-warning py-1 px-2 mt-2 mb-0 small">
                                          <i className="ri-information-line me-1"></i>
                                          <strong>Aguardando conclusão de:</strong>
                                          <ul className="mb-0 mt-1 ps-3">
                                            {dependencyCheck.pendingItems.map(dep => (
                                              <li key={dep.checkListItemId}>{dep.itemName}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span className="badge bg-primary-subtle text-primary">
                                      {item.responsible}
                                    </span>
                                  </td>
                                  <td style={{ padding: 0 }}>
                                    {(() => {
                                      const deadlineStatus = calculateDeadlineStatus(item);

                                      // Map status to ribbon classes
                                      const getRibbonClass = (status: string) => {
                                        switch (status) {
                                          case 'completed':
                                            return 'ribbon-success';
                                          case 'late':
                                            return 'ribbon-danger';
                                          case 'warning':
                                            return 'ribbon-warning';
                                          case 'on-time':
                                            return 'ribbon-info';
                                          case 'waiting':
                                            return 'ribbon-secondary';
                                          default:
                                            return 'ribbon-secondary';
                                        }
                                      };

                                      const getStatusIcon = (status: string) => {
                                        switch (status) {
                                          case 'completed':
                                            return 'ri-checkbox-circle-line';
                                          case 'late':
                                            return 'ri-alarm-warning-line';
                                          case 'warning':
                                            return 'ri-time-line';
                                          case 'on-time':
                                            return 'ri-timer-line';
                                          case 'waiting':
                                            return 'ri-hourglass-line';
                                          default:
                                            return 'ri-information-line';
                                        }
                                      };

                                      const getStatusLabel = (status: string) => {
                                        switch (status) {
                                          case 'completed':
                                            return t('Concluído');
                                          case 'late':
                                            return t('Atrasado');
                                          case 'warning':
                                            return t('Alerta');
                                          case 'on-time':
                                            return t('No Prazo');
                                          case 'waiting':
                                            return t('Aguardando');
                                          default:
                                            return '-';
                                        }
                                      };

                                      return (
                                        <div
                                          className="ribbon-box right overflow-hidden"
                                          style={{
                                            position: 'relative',
                                            minHeight: '55px',
                                            padding: '6px 10px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          {/* Compact Trending Ribbon on Right */}
                                          <div
                                            className={`ribbon ${getRibbonClass(deadlineStatus.status)} trending-ribbon`}
                                            style={{
                                              fontSize: '9px',
                                              padding: '2px 6px',
                                              width: '75px',
                                              textAlign: 'center',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <i className={`${getStatusIcon(deadlineStatus.status)} text-white align-bottom me-1`} style={{ fontSize: '11px', flexShrink: 0 }}></i>
                                            <span
                                              className="trending-ribbon-text"
                                              style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1
                                              }}
                                            >
                                              {getStatusLabel(deadlineStatus.status)}
                                            </span>
                                          </div>

                                          {/* Deadline Info */}
                                          <div style={{ marginTop: '0px' }}>
                                            <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                              {item.deadlineValue} {item.deadlineUnit}
                                            </small>
                                            <small className={`d-block ${deadlineStatus.colorClass}`} style={{ fontSize: '11px' }}>
                                              {deadlineStatus.label}
                                            </small>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="text-center">
                                    <div className="form-check form-switch form-switch-md d-flex justify-content-center align-items-center">
                                      <Input
                                        type="checkbox"
                                        className="form-check-input code-switcher"
                                        checked={item.isDisregarded || false}
                                        onChange={() => handleToggleDisregard(item)}
                                        disabled={updatingItems[item.checkListItemId] || (item.isDisregarded && !canReopenCompletedTasks)}
                                        title={
                                          item.isDisregarded && !canReopenCompletedTasks
                                            ? t('Item desconsiderado e bloqueado')
                                            : item.isDisregarded && canReopenCompletedTasks
                                            ? t('Você pode reabrir este item')
                                            : t('Marcar como desconsiderado')
                                        }
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type="text"
                                      bsSize="sm"
                                      placeholder={t('Adicionar observação...')}
                                      value={item.notes || ''}
                                      onChange={(e) => handleNotesChange(item, e.target.value)}
                                      disabled={item.isCompleted}
                                      data-item-id={item.checkListItemId}
                                    />
                                    {(() => {
                                      const charCount = (item.notes || '').trim().length;
                                      const minRequired = 15;
                                      const meetsRequirement = charCount >= minRequired;

                                      return (
                                        <small
                                          className={`d-block mt-1 ${meetsRequirement ? 'text-success fw-semibold' : 'text-muted'}`}
                                          style={{ fontSize: '0.75rem' }}
                                        >
                                          {charCount}/{minRequired} caracteres
                                          {!meetsRequirement && (
                                            <span className="text-danger">
                                              {' '}- <i className="ri-information-line"></i> {t('Mínimo 15 para desconsiderar')}
                                            </span>
                                          )}
                                          {meetsRequirement && (
                                            <span>
                                              {' '}<i className="ri-checkbox-circle-line"></i> {t('OK para desconsiderar')}
                                            </span>
                                          )}
                                        </small>
                                      );
                                    })()}
                                    {item.observation && (
                                      <small className="text-muted d-block mt-1">
                                        <i className="ri-information-line me-1"></i>
                                        {item.observation}
                                      </small>
                                    )}
                                  </td>
                                </tr>
                              );
                              })}
                            </tbody>
                          </Table>
                          </div>
                        </CardBody>
                      </Collapse>
                    </Card>
                  );
                })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <Button color="light" onClick={onPrevious} disabled={isSubmitting}>
          <i className="ri-arrow-left-line me-1"></i>
          {t('Anterior')}
        </Button>
        <Button color="success" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {isFinalPhase ? t('Finalizando...') : t('Salvando...')}
            </>
          ) : (
            <>
              {isFinalPhase ? (
                <>
                  <i className="ri-check-line me-1"></i>
                  {t('Finalizar Cadastro')}
                </>
              ) : (
                <>
                  {t('Próximo')}
                  <i className="ri-arrow-right-line ms-1"></i>
                </>
              )}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Phase3CheckListForm;
